import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { VoicePart } from './types';

/** The six event types. A concert is one type, not the general model. */
export type EventType = 'CONCERT' | 'FESTIVAL' | 'COMPETITION' | 'CEREMONY' | 'RECORDING' | 'SOCIAL';
export const EVENT_TYPES: readonly EventType[] = ['CONCERT', 'FESTIVAL', 'COMPETITION', 'CEREMONY', 'RECORDING', 'SOCIAL'];

export type FieldKind = 'TEXT' | 'DATE' | 'MINUTES';
export interface TypeInfo {
  type: EventType;
  readinessTracking: boolean;
  limitedLineup: boolean;
  fields: { key: string; kind: FieldKind }[];
}

/** Availability is yes or no. */
export type Answer = 'ATTENDING' | 'NOT_ATTENDING';
export type ItemKind = 'SONG' | 'INTERMISSION' | 'SPEECH';
export type Participation = 'IN' | 'NOT_IN_LINEUP' | 'EXCLUDED';
export type EventDetails = Record<string, string | number>;

export interface Deadline {
  /** "reply", "submission", or a type field such as "preselectionDeadline". */
  code: string;
  date: string;
  songTitle: string | null;
}

export interface EventSummary {
  id: string;
  type: EventType;
  name: string;
  startsOn: string;
  endsOn: string | null;
  venue: string | null;
  limitedLineup: boolean;
  readinessTracking: boolean;
  lineupCount: number;
  attending: number;
  notAttending: number;
  noReply: number;
  songCount: number;
  readyAll: number;
  nextDeadline: Deadline | null;
}

export interface ProgramItem {
  id: string;
  kind: ItemKind;
  songId: string | null;
  songTitle: string | null;
  songComposer: string | null;
  durationSec: number | null;
  text: string | null;
  submissionRequested: boolean;
  submissionDeadline: string | null;
}

/** A programme item as the chorist sees it, with their own grade on a song. */
export interface MyProgramItem {
  id: string;
  kind: ItemKind;
  songId: string | null;
  songTitle: string | null;
  songComposer: string | null;
  durationSec: number | null;
  text: string | null;
  submissionRequested: boolean;
  submissionDeadline: string | null;
  grade: 'READY' | 'ALMOST' | 'NEEDS_WORK' | null;
  submittedSinceRequest: boolean;
}

/** A singer in the line-up as other members see it: name, part and section led only. */
export interface LineupMember {
  userId: string;
  displayName: string;
  part: VoicePart | null;
  leadsSection: VoicePart | null;
}

/** One event for a chorist: read-only apart from their own answer. */
export interface MyEventDetail {
  event: MyEvent;
  details: Record<string, string | number>;
  program: MyProgramItem[];
  /** Who sings, only when the chorist is one of them. */
  lineup: LineupMember[];
}

export interface MemberStatus {
  userId: string;
  displayName: string;
  part: VoicePart | null;
  /** The section this member leads as Grup Lideri, or null. */
  leadsSection: VoicePart | null;
  inLineup: boolean;
  answer: Answer | null;
  note: string | null;
  answeredAt: string | null;
  excluded: boolean;
  exclusionReason: string | null;
  readySongs: number;
}

export interface EventDetail {
  id: string;
  type: EventType;
  name: string;
  startsOn: string;
  endsOn: string | null;
  venue: string | null;
  callAndDress: string | null;
  readinessTracking: boolean;
  limitedLineup: boolean;
  replyDeadline: string | null;
  details: EventDetails;
  summary: EventSummary;
  program: ProgramItem[];
  members: MemberStatus[];
}

export interface MySong {
  songId: string;
  title: string;
  composer: string | null;
  grade: 'READY' | 'ALMOST' | 'NEEDS_WORK' | null;
  submissionRequested: boolean;
  submissionDeadline: string | null;
  submittedSinceRequest: boolean;
}

export interface MyEvent {
  id: string;
  type: EventType;
  name: string;
  startsOn: string;
  endsOn: string | null;
  venue: string | null;
  callAndDress: string | null;
  replyDeadline: string | null;
  readinessTracking: boolean;
  limitedLineup: boolean;
  participation: Participation;
  exclusionReason: string | null;
  answer: Answer | null;
  note: string | null;
  answeredAt: string | null;
  songs: MySong[];
}

export interface EventRequest {
  type: EventType;
  name: string;
  startsOn: string;
  endsOn: string | null;
  venue: string | null;
  callAndDress: string | null;
  readinessTracking: boolean;
  limitedLineup: boolean;
  replyDeadline: string | null;
  details: EventDetails;
}

export interface ItemRequest {
  kind: ItemKind;
  songId?: string | null;
  text?: string | null;
  durationSec?: number | null;
  submissionRequested?: boolean;
  submissionDeadline?: string | null;
}

const eventsKey = (choirId: string) => ['events', choirId] as const;
const eventKey = (choirId: string, eventId: string) => ['event', choirId, eventId] as const;
const myEventsKey = (choirId: string) => ['myEvents', choirId] as const;
// Under myEventsKey, so a reply refreshes the event page too.
const myEventKey = (choirId: string, eventId: string) => ['myEvents', choirId, eventId] as const;

export function useEventTypes() {
  return useQuery({ queryKey: ['eventTypes'], queryFn: () => api<TypeInfo[]>('/api/event-types'), staleTime: Infinity });
}

export function useEvents(choirId: string | undefined) {
  return useQuery({
    queryKey: eventsKey(choirId ?? ''),
    queryFn: () => api<EventSummary[]>(`/api/choirs/${choirId}/events`),
    enabled: !!choirId,
  });
}

export function useEvent(choirId: string | undefined, eventId: string | undefined) {
  return useQuery({
    queryKey: eventKey(choirId ?? '', eventId ?? ''),
    queryFn: () => api<EventDetail>(`/api/choirs/${choirId}/events/${eventId}`),
    enabled: !!choirId && !!eventId,
  });
}

/** After any change to one event: refresh it, the list and members' views. */
function useEventSaved(choirId: string) {
  const queryClient = useQueryClient();
  return (event: EventDetail) => {
    queryClient.setQueryData(eventKey(choirId, event.id), event);
    void queryClient.invalidateQueries({ queryKey: eventsKey(choirId) });
    void queryClient.invalidateQueries({ queryKey: myEventsKey(choirId) });
  };
}

export function useSaveEvent(choirId: string) {
  const saved = useEventSaved(choirId);
  return useMutation({
    mutationFn: ({ id, request }: { id?: string; request: EventRequest }) =>
      id
        ? api<EventDetail>(`/api/choirs/${choirId}/events/${id}`, { method: 'PUT', json: request })
        : api<EventDetail>(`/api/choirs/${choirId}/events`, { method: 'POST', json: request }),
    onSuccess: saved,
  });
}

export function useDeleteEvent(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (eventId: string) => api<void>(`/api/choirs/${choirId}/events/${eventId}`, { method: 'DELETE' }),
    onSuccess: (_, eventId) => {
      queryClient.removeQueries({ queryKey: eventKey(choirId, eventId) });
      void queryClient.invalidateQueries({ queryKey: eventsKey(choirId) });
      void queryClient.invalidateQueries({ queryKey: myEventsKey(choirId) });
    },
  });
}

export function useSetLineup(choirId: string) {
  const saved = useEventSaved(choirId);
  return useMutation({
    mutationFn: ({ eventId, userIds }: { eventId: string; userIds: string[] }) =>
      api<EventDetail>(`/api/choirs/${choirId}/events/${eventId}/lineup`, { method: 'PUT', json: { userIds } }),
    onSuccess: saved,
  });
}

export function useSetProgram(choirId: string) {
  const saved = useEventSaved(choirId);
  return useMutation({
    mutationFn: ({ eventId, items }: { eventId: string; items: ItemRequest[] }) =>
      api<EventDetail>(`/api/choirs/${choirId}/events/${eventId}/program`, { method: 'PUT', json: { items } }),
    onSuccess: saved,
  });
}

/** Leave a member out of an event (reason shown only to them), or take them back. */
export function useExclusion(choirId: string) {
  const saved = useEventSaved(choirId);
  return useMutation({
    mutationFn: ({ eventId, userId, exclude, reason }: { eventId: string; userId: string; exclude: boolean; reason?: string }) =>
      api<EventDetail>(`/api/choirs/${choirId}/events/${eventId}/exclusions/${userId}`, {
        method: exclude ? 'PUT' : 'DELETE',
        json: exclude ? { reason: reason ?? null } : undefined,
      }),
    onSuccess: saved,
  });
}

export function useMyEvents(choirId: string | undefined) {
  return useQuery({
    queryKey: myEventsKey(choirId ?? ''),
    queryFn: () => api<MyEvent[]>(`/api/choirs/${choirId}/me/events`),
    enabled: !!choirId,
  });
}

export function useMyEvent(choirId: string | undefined, eventId: string | undefined) {
  return useQuery({
    queryKey: myEventKey(choirId ?? '', eventId ?? ''),
    queryFn: () => api<MyEventDetail>(`/api/choirs/${choirId}/me/events/${eventId}`),
    enabled: !!choirId && !!eventId,
  });
}

export function useReply(choirId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, answer, note }: { eventId: string; answer: Answer; note: string | null }) =>
      api<MyEvent>(`/api/choirs/${choirId}/events/${eventId}/reply`, { method: 'PUT', json: { answer, note } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: myEventsKey(choirId) }),
  });
}
